import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { from, throwError, switchMap, catchError } from 'rxjs';

/**
 * Check if error indicates refresh token is expired
 */
function isRefreshTokenExpiredError(error: any): boolean {
  return (
    error?.error === 'invalid_grant' ||
    error?.error_description?.includes('Token is not active') ||
    error?.error_description?.includes('Refresh token expired')
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);

  // Skip interceptor for non-API calls
  if (!req.url.includes('/api/')) {
    return next(req);
  }

  // If not logged in, proceed without token (will likely get 401)
  if (!keycloak.isLoggedIn()) {
    return next(req);
  }

  // Check if token needs refresh (expires in less than 30 seconds)
  const keycloakInstance = keycloak.getKeycloakInstance();
  const token = keycloakInstance.token;

  if (!token) {
    return next(req);
  }

  // Check token expiration
  const tokenExpiry = keycloakInstance.tokenParsed?.exp;
  const currentTime = Math.floor(Date.now() / 1000);
  const tokenExpiresInSeconds = tokenExpiry ? tokenExpiry - currentTime : 0;

  // If token expires in less than 30 seconds, try to refresh
  if (tokenExpiresInSeconds < 30) {
    return from(keycloak.updateToken(30)).pipe(
      switchMap((refreshed) => {
        if (refreshed) {
          const newToken = keycloak.getKeycloakInstance().token;
          if (newToken) {
            const authReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`),
            });
            return next(authReq);
          }
        }

        // If refresh failed but token is still valid, use existing token
        const authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`),
        });
        return next(authReq);
      }),
      catchError((error) => {
        console.error('Token refresh failed:', error);

        // If refresh token is expired, logout cleanly
        if (isRefreshTokenExpiredError(error)) {
          console.warn('[AuthInterceptor] Refresh token expired, logging out...');
          keycloak.logout(window.location.origin);
          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'Session expired',
                status: 401,
                statusText: 'Unauthorized',
              }),
          );
        }

        // If refresh failed, try with existing token anyway
        const authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`),
        });
        return next(authReq).pipe(
          catchError((httpError: HttpErrorResponse) => {
            if (httpError.status === 401) {
              // Redirect to login for other auth errors
              keycloak.login();
              return throwError(() => httpError);
            }
            return throwError(() => httpError);
          }),
        );
      }),
    );
  }

  // Token is still valid, use it
  const authReq = req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Try to refresh token on 401 error
        return from(keycloak.updateToken(30)).pipe(
          switchMap((refreshed) => {
            if (refreshed) {
              const newToken = keycloak.getKeycloakInstance().token;
              if (newToken) {
                const retryReq = req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${newToken}`),
                });
                return next(retryReq);
              }
            }

            keycloak.login();
            return throwError(() => error);
          }),
          catchError((refreshError) => {
            console.error('Token refresh failed after 401:', refreshError);

            // If refresh token is expired, logout cleanly
            if (isRefreshTokenExpiredError(refreshError)) {
              console.warn('[AuthInterceptor] Refresh token expired after 401, logging out...');
              keycloak.logout(window.location.origin);
              return throwError(
                () =>
                  new HttpErrorResponse({
                    error: 'Session expired',
                    status: 401,
                    statusText: 'Unauthorized',
                  }),
              );
            }

            // Redirect to login for other auth errors
            keycloak.login();
            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
};
